import React, { useEffect, useRef } from "react";
import SimpleBar from "simplebar-react";
import { DateOnly, DateTime } from "./Date";

export type TimelineStepState = "done" | "active" | "pending" | "skipped";

export interface TimelineStep {
  label: string;
  timestamp?: string | null;
  dateOnly?: boolean;
  actor?: string | null;
  by?: string | null;
  state: TimelineStepState;
  /** Override the default color for this step — any bootstrap contextual name (e.g. "danger" for a cancelled step). */
  color?: string;
  /** Override the default icon for this step (Remix icon class). Falls back to a sensible default per state. */
  icon?: string;
}

const DEFAULT_STATE_COLOR: Record<TimelineStepState, string> = {
  done: "success",
  active: "primary",
  pending: "secondary",
  skipped: "danger",
};

const DEFAULT_STATE_ICON: Record<TimelineStepState, string> = {
  done: "ri-check-line",
  active: "ri-time-line",
  pending: "ri-more-line",
  skipped: "ri-close-line",
};

export function Timeline({
  steps,
  maxHeight = 320,
}: {
  steps: TimelineStep[];
  maxHeight?: number;
}) {
  const simpleBarRef = useRef<React.ComponentRef<typeof SimpleBar>>(null);

  useEffect(() => {
    const el = simpleBarRef.current?.getScrollElement();
    if (el) el.scrollTop = el.scrollHeight;
  }, [steps]);

  return (
    <SimpleBar ref={simpleBarRef} style={{ maxHeight }}>
      <style>{`
        .timeline-pulse-dot {
          animation: timeline-pulse 1.6s ease-out infinite;
        }
        @keyframes timeline-pulse {
          0% {
            box-shadow: 0 0 0 0 rgba(var(--timeline-pulse-rgb), 0.5);
          }
          70% {
            box-shadow: 0 0 0 7px rgba(var(--timeline-pulse-rgb), 0);
          }
          100% {
            box-shadow: 0 0 0 0 rgba(var(--timeline-pulse-rgb), 0);
          }
        }
      `}</style>
      <div className="py-1 px-3">
        {steps.map((step, i) => {
          const isLast = i === steps.length - 1;
          const effectiveState: TimelineStepState = step.state;
          const color = step.color ?? DEFAULT_STATE_COLOR[effectiveState];
          const icon = step.icon ?? DEFAULT_STATE_ICON[effectiveState];
          const showConnector = !isLast || effectiveState === "active";
          const labelColorClass = step.color
            ? `text-${step.color}`
            : effectiveState === "pending"
              ? "text-muted"
              : "text-body";

          return (
            <div key={`${step.label}-${i}`} className="d-flex gap-3">
              <div
                className="d-flex flex-column align-items-center flex-shrink-0"
                style={{ width: 22 }}
              >
                <div
                  className={`rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 bg-${color} text-white ${effectiveState === "active" ? "timeline-pulse-dot" : ""}`}
                  style={
                    {
                      width: 22,
                      height: 22,
                      position: "relative",
                      zIndex: 1,
                      "--timeline-pulse-rgb": `var(--vz-${color}-rgb)`,
                    } as React.CSSProperties
                  }
                >
                  <i className={icon} style={{ fontSize: 11 }} />
                </div>
                {showConnector && (
                  <div
                    className={`flex-grow-1 border-start border-2 border-${color}`}
                    style={{
                      width: 2,
                      minHeight: 28,
                      marginTop: 2,
                      marginBottom: 2,
                    }}
                  />
                )}
              </div>

              <div className={`${showConnector ? "pb-3" : "pb-0"} min-w-0`}>
                <p className={`mb-0 fw-semibold small ${labelColorClass}`}>
                  {step.label}
                </p>
                {step.by != null ? (
                  <p
                    className="mb-1 text-muted d-flex align-items-center gap-1"
                    style={{ fontSize: "0.75rem" }}
                  >
                    <i
                      className="ri-user-line flex-shrink-0"
                      style={{ fontSize: "0.75rem" }}
                    />
                    <span>{step.by}</span>
                  </p>
                ) : null}
                {step.actor ? (
                  <p
                    className="mb-1 text-muted d-flex align-items-start gap-1"
                    style={{ fontSize: "0.75rem" }}
                  >
                    <i
                      className="ri-message-2-line flex-shrink-0"
                      style={{ fontSize: "0.75rem", marginTop: 1 }}
                    />
                    <span>{step.actor}</span>
                  </p>
                ) : null}
                {step.timestamp ? (
                  <p
                    className="mb-0 text-muted d-flex align-items-center gap-1"
                    style={{ fontSize: "0.75rem" }}
                  >
                    <i
                      className="ri-calendar-line flex-shrink-0"
                      style={{ fontSize: "0.75rem" }}
                    />
                    {step.dateOnly ? (
                      <DateOnly date={step.timestamp} />
                    ) : (
                      <DateTime date={step.timestamp} />
                    )}
                  </p>
                ) : effectiveState === "active" ? (
                  <p
                    className="mb-0 text-muted fst-italic d-flex align-items-center gap-1"
                    style={{ fontSize: "0.75rem" }}
                  >
                    <i
                      className="ri-loader-line flex-shrink-0"
                      style={{ fontSize: "0.75rem" }}
                    />
                    In progress
                  </p>
                ) : effectiveState === "pending" ? (
                  <p
                    className="mb-0 text-muted fst-italic d-flex align-items-center gap-1"
                    style={{ fontSize: "0.75rem" }}
                  >
                    <i
                      className="ri-time-line flex-shrink-0"
                      style={{ fontSize: "0.75rem" }}
                    />
                    Pending
                  </p>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    </SimpleBar>
  );
}
